import { useEffect, useRef, useState } from 'react';
import { useParams, Outlet, useNavigate } from 'react-router';
import { useFormConfig } from '../form-config/hooks/useFormConfig';
import { useDebouncedSave } from '../form-config/hooks/useDebouncedSave';
import { useFieldValidator } from '../form-config/hooks/useFieldValidator';
import { useFormConfigStore } from '../form-config/state/store';
import { useJobType } from '../hooks/useJobTypes';
import { DraftBanner } from '../form-config/DraftBanner';
import { PublishModal } from '../form-config/PublishModal';
import { formConfigApi, type JobTypeVersion } from '../form-config/api/client';
import { ApiError } from '@/lib/api-client';
import { computeVersionDiff, type ValidationError, type JobTypeVersionContent } from '@capture/shared';
import { ConfirmModal } from '@/components/ConfirmModal';
import { TextPromptModal } from '@/components/TextPromptModal';
import { toast } from '@/components/Toaster';
import { navigateToLinkTarget } from '../lib/link-target-path';
import type { WorkspaceContext } from './workspace-context';
import './JobVersionWorkspace.css';

// F-055 — publish reloads the page, so the "published" confirmation is carried
// across the reload by this one-shot sessionStorage flag and fired as a toast on
// the next mount.
const PUBLISH_SUCCESS_FLAG = 'capture.publishSuccess';

/** A single entry from the export conformance gate (409 response body). */
interface ConformanceEntry {
  formatId: string;
  formatName: string;
  /** The form whose mappings produced this entry's slot violations (optional — absent for non-form-scoped entries). */
  formId?: string;
  formName?: string;
  level: 'hard' | 'soft';
  violations: Array<{ kind: string; field: string; detail?: string }>;
}

export function JobVersionWorkspace() {
  const { jobTypeId = '' } = useParams<{ jobTypeId: string }>();
  const navigate = useNavigate();
  const { loading, error, versions, autoForkIfPublished } = useFormConfig(jobTypeId);
  useDebouncedSave();
  const validator = useFieldValidator();
  useStoreAutoFork(autoForkIfPublished);

  // The workspace doesn't fetch jobType for its own title, but PublishModal is
  // gated on the job type's name (mirrors FormConfigPage's original gate).
  const { jobType } = useJobType(jobTypeId);

  const version = useFormConfigStore((s) => s.version);
  const content = useFormConfigStore((s) => s.content);
  const saving = useFormConfigStore((s) => s.saving);
  const lastSavedAt = useFormConfigStore((s) => s.lastSavedAt);
  // F-032 — a stale-rejected autosave suspends saving and shows the reload banner.
  const conflicted = useFormConfigStore((s) => s.conflicted);

  // F-055 — surface the post-publish confirmation once, after the reload.
  useEffect(() => {
    if (sessionStorage.getItem(PUBLISH_SUCCESS_FLAG)) {
      sessionStorage.removeItem(PUBLISH_SUCCESS_FLAG);
      toast.success('Published');
    }
  }, []);

  const [publishOpen, setPublishOpen] = useState(false);
  const [serverErrors, setServerErrors] = useState<ValidationError[] | undefined>(undefined);
  const [discardOpen, setDiscardOpen] = useState(false);

  // FR-001 Task 12 — conformance gate 409 handling.
  // Hard block: show a non-bypassable dialog.
  // Soft override: show a reason prompt, then re-publish.
  const [conformanceBlock, setConformanceBlock] = useState<ConformanceEntry[] | null>(null);
  const [conformanceOverride, setConformanceOverride] = useState<{
    entries: ConformanceEntry[];
    originalBody: { publishNote: string; typedConfirmation: string };
  } | null>(null);

  function handleDiscard() {
    setDiscardOpen(true);
  }

  async function handleConfirmDiscard() {
    setDiscardOpen(false);
    // F-041 — another tab may have already discarded (or published) the draft;
    // this tab's copy is then stale and the DELETE fails. Reload REGARDLESS:
    // it resyncs to the real server state in every case (draft gone → published
    // view; genuine transient failure → the still-existing draft reloads).
    try {
      await formConfigApi.discardDraft(jobTypeId);
    } catch {
      /* stale local draft state — reload below resyncs */
    }
    window.location.reload();
  }

  async function handlePublish(body: { publishNote: string; typedConfirmation: string; overrideReason?: string }) {
    try {
      const result = await formConfigApi.publishDraft(jobTypeId, version!.id, body);
      setPublishOpen(false);
      // F-038 — surface the OCR caller-profile sync outcome. The publish itself
      // succeeded (OCR is a soft dependency), but a failed sync means extraction
      // is stale, so the operator must be told before we reload to the published
      // view. A successful/skipped sync reloads silently as before.
      if (result?.ocrProfileSync?.status === 'failed') {
        window.alert(
          `Published — but the OCR profile sync FAILED:\n${result.ocrProfileSync.error ?? 'unknown error'}\n\nOCR extraction may be stale. Re-publish once the OCR service is reachable.`,
        );
      }
      // F-055 — carry the success confirmation across the reload (a toast fired
      // here would be wiped by the reload). The banner already clears in-place
      // now that the mount-time re-key no longer spuriously re-forks a draft.
      sessionStorage.setItem(PUBLISH_SUCCESS_FLAG, '1');
      window.location.reload();
    } catch (e) {
      // Surface every API failure in the modal — re-throwing inside an async
      // event handler becomes an unhandled rejection (silent in production).
      // Truly unexpected throws (programmer bugs upstream) still re-throw. (#84)
      if (!(e instanceof ApiError)) throw e;
      const errBody = e.body as {
        error?: string;
        errors?: ValidationError[];
        conformance?: ConformanceEntry[];
      } | null;

      // FR-001: hard conformance block — no bypass. Close publish modal first so
      // Radix doesn't aria-hide the block dialog behind its portal overlay.
      if (e.status === 409 && errBody?.error === 'export_conformance_failed') {
        setPublishOpen(false);
        setConformanceBlock(errBody.conformance ?? []);
        return;
      }

      // FR-001: soft conformance block — collect an override reason, then re-publish.
      // Close publish modal first for the same Radix portal reason.
      if (e.status === 409 && errBody?.error === 'export_conformance_override_required') {
        setPublishOpen(false);
        setConformanceOverride({
          entries: errBody.conformance ?? [],
          originalBody: { publishNote: body.publishNote, typedConfirmation: body.typedConfirmation },
        });
        return;
      }

      if (e.status === 400 && errBody?.errors) {
        setServerErrors(errBody.errors);
        return;
      }
      const message = errBody?.error ?? e.message ?? 'Publish failed';
      setServerErrors([{ code: `publish_failed_${e.status}`, severity: 'error', path: [], message }]);
    }
  }

  // R-71 — called when the operator clicks a publish error that carries
  // linkTarget. Closes the modal, then delegates the tab→surface routing +
  // scroll-into-view to the shared navigateToLinkTarget (lib/link-target-path
  // — also used by ImportFromGithubSection's resolution report, S-6 Task 8
  // ruling) so the two callers can't drift on which tabs live where.
  function handleErrorClick(target: NonNullable<ValidationError['linkTarget']>) {
    setPublishOpen(false);
    setServerErrors(undefined);
    navigateToLinkTarget(navigate, jobTypeId, target);
  }

  return (
    <div className="job-version-workspace">
      {version && (
        <DraftBanner
          data-testid="draft-banner"
          state={version.state}
          saving={saving}
          errorCount={validator.errorCount}
          errors={validator.errors}
          lastSavedAt={lastSavedAt}
          conflicted={conflicted}
          onReload={() => window.location.reload()}
          onPublish={() => setPublishOpen(true)}
          onDiscard={handleDiscard}
          onErrorClick={handleErrorClick}
        />
      )}

      <Outlet context={{ jobTypeId, versions, loading, error } satisfies WorkspaceContext} />

      {publishOpen && jobType?.name && content && (
        <PublishModal
          jobTypeName={jobType.name}
          diff={computeDiffForPublish(versions, content)}
          serverErrors={serverErrors}
          onCancel={() => {
            setPublishOpen(false);
            setServerErrors(undefined);
          }}
          onPublish={handlePublish}
          onErrorClick={handleErrorClick}
        />
      )}
      <ConfirmModal
        open={discardOpen}
        title="Discard all unsaved changes?"
        description="Your draft will revert to the last published version. This cannot be undone."
        confirmLabel="Discard"
        destructive
        onConfirm={handleConfirmDiscard}
        onCancel={() => setDiscardOpen(false)}
      />

      {/* FR-001 Task 12 — hard conformance block (no bypass) */}
      <ConfirmModal
        open={conformanceBlock !== null}
        title="Cannot publish — export conformance failed"
        description={
          conformanceBlock
            ? conformanceBlock
                .map(
                  (entry) =>
                    // F-101 — each violation now carries its `detail`, the
                    // actionable sentence ("map at least one field…"). The key
                    // alone read as "1 issue(s) — sql" and told the operator
                    // nothing; the key is kept as the locator ahead of it.
                    `${entry.formName ? entry.formName + ' — ' : ''}${entry.formatName}: ${entry.violations.length} issue(s)\n${entry.violations
                      .map((v) => `  • ${v.detail ? `${v.field} — ${v.detail}` : v.field}`)
                      .join('\n')}`,
                )
                .join('\n')
            : undefined
        }
        confirmLabel="Close"
        hideCancel
        onConfirm={() => setConformanceBlock(null)}
        onCancel={() => setConformanceBlock(null)}
      />

      {/* FR-001 Task 12 — soft conformance override (collect reason, re-publish) */}
      <TextPromptModal
        open={conformanceOverride !== null}
        title="Export conformance — override required"
        description={
          conformanceOverride
            ? `The following formats have warnings:\n${conformanceOverride.entries
                .map((entry) => `${entry.formName ? entry.formName + ' — ' : ''}${entry.formatName}: ${entry.violations.map((v) => v.field).join(', ')}`)
                .join('\n')}\n\nEnter a supervisor reason to override and publish.`
            : undefined
        }
        placeholder="Reason for override…"
        submitLabel="Override & Publish"
        validate={(v) => (v.trim().length === 0 ? 'A reason is required to override' : null)}
        onConfirm={(reason) => {
          const saved = conformanceOverride;
          setConformanceOverride(null);
          if (saved) {
            void persistDurabilityAck(saved.entries).then(() =>
              handlePublish({ ...saved.originalBody, overrideReason: reason }),
            );
          }
        }}
        onCancel={() => setConformanceOverride(null)}
      />
    </div>
  );
}

/**
 * E-038 — the F-012 ephemeral-filesystem-basePath SOFT warning used to
 * re-fire on every publish, demanding a fresh override reason forever. Once
 * the operator has typed a reason for it, mark THIS destination's config
 * with `_ephemeralPathAckedFor: <basePath>` (additive key, no schema
 * migration — the API's `preflightDurabilityForContent` skips the warning
 * when it matches the live basePath, and re-arms automatically the moment
 * basePath changes). Flushes the write immediately (rather than waiting on
 * the 800ms debounce) because the publish retry that follows reads the
 * draft straight from the DB. Best-effort: a flush failure just means the
 * next publish re-demands the reason too — no worse than today's behavior.
 */
async function persistDurabilityAck(entries: ConformanceEntry[]): Promise<void> {
  const durabilityDestIds = entries
    .filter((e) => e.formatId.startsWith('durability:'))
    .map((e) => e.formatId.slice('durability:'.length));
  if (durabilityDestIds.length === 0) return;

  const store = useFormConfigStore.getState();
  for (const destId of durabilityDestIds) {
    const dest = store.contentV3?.exportDestinations.find((d) => d.id === destId);
    const basePath = (dest?.config as { basePath?: unknown } | undefined)?.basePath;
    if (dest && typeof basePath === 'string') {
      store.updateExportDestination(destId, {
        config: { ...dest.config, _ephemeralPathAckedFor: basePath },
      });
    }
  }

  const fresh = useFormConfigStore.getState();
  if (!fresh.version || !fresh.contentV3) return;
  try {
    const updated = await formConfigApi.updateDraft(
      fresh.version.id,
      fresh.contentV3,
      fresh.version.contentRevision,
    );
    useFormConfigStore.getState().markSaved(updated.contentRevision);
  } catch {
    // best-effort — see docstring above.
  }
}

function computeDiffForPublish(versions: JobTypeVersion[], content: JobTypeVersionContent) {
  const publishedContent = versions.find((v) => v.state === 'published')?.content ?? null;
  if (publishedContent) return computeVersionDiff(publishedContent, content);
  return {
    fieldsAdded: content.fields.map((f) => f.name),
    fieldsRemoved: [],
    fieldsChanged: [],
    pipelineChanged: false,
    headerMappingChanged: false,
    rulesChanged: false,
    rulesDiff: [],
    exportChanged: false,
    summary: [],
  };
}

function useStoreAutoFork(autoFork: () => Promise<void>) {
  // Hold the latest autoFork in a ref so the store subscription can be
  // registered exactly once (on mount) yet always call the current closure.
  const autoForkRef = useRef(autoFork);
  autoForkRef.current = autoFork;

  useEffect(() => {
    // Subscribe ONCE and clean up on unmount. The previous version called
    // useFormConfigStore.subscribe() directly in the component body, which
    // registered a fresh listener on every render with no unsubscribe — so a
    // single published→dirty transition fired autoFork once per leaked
    // listener, dispatching a storm of concurrent createDraft POSTs that raced
    // on the (jobTypeId, versionNumber) unique key and 500'd. One listener,
    // one fork.
    const unsubscribe = useFormConfigStore.subscribe((state, prev) => {
      if (state.dirty && !prev.dirty && state.version?.state === 'published') {
        void autoForkRef.current();
      }
    });
    return unsubscribe;
  }, []);
}
